import {
  Fragment,
  lazy,
  memo,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from "react";

import { Transition } from "@headlessui/react";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { CartDrawer } from "@cart/CartDrawer/CartDrawer.data";
import { CartDrawerAnalytics } from "@cart/CartDrawer/CartDrawerAnalytics.data";
import { CartDrawerBackdrop } from "@cart/CartDrawer/CartDrawerBackdrop.view";
import { useAddToCartManager } from "@react/hooks/useAddToCartManager";
import {
  STOREFRONT_TRANSITION_EASING,
  STOREFRONT_TRANSITION_DURATION,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { type TablescapeState } from "@tablescape/state";
import { tw } from "classnames";

const CartGiftNoteDrawer = lazy(
  () =>
    import(
      /* webpackExclude: /fixtures|stories|test/ */
      "@cart/CartGiftNoteDrawer/index"
    )
);
const CartProductVariantDrawer = lazy(
  () =>
    import(
      /* webpackExclude: /fixtures|stories|test/ */
      "@cart/CartProductVariantDrawer/index"
    )
);
const CartGiftWithPurchaseDrawer = lazy(
  () =>
    import(
      /* webpackExclude: /fixtures|stories|test/ */
      "@cart/CartGiftWithPurchaseDrawer/index"
    )
);

const styles = {
  container: tw(
    "flex",
    "justify-end",
    "border-none",
    "bg-transparent",
    "p-0",
    "pointer-events-auto",
    "w-screen"
  ),
  drawer: {
    enter: tw(
      "transition",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    enterFrom: tw("translate-x-full"),
    enterTo: tw("translate-x-0"),
    leave: tw(
      "transition",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    leaveFrom: tw("translate-x-0"),
    leaveTo: tw("translate-x-full"),
  },
  wrapper: tw("relative"),
};

const CartDrawerManagerBase = () => {
  const {
    ui: {
      cartDrawerOpen,
      giftNoteDrawerOpen,
      giftWithPurchaseDrawerOpen,
      productVariantDrawerOpen,
    },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );

  const { activeProduct, toggleCartDrawer } = useCartActiveProduct();

  /**
   * A custom hook that manages the add to cart process.
   * This is the single most important hook in the Cart scope
   * It is responsible for:
   * - adding items to the cart
   * - adding custom cart attributes
   * - updating the cart item count
   * - updating the cart id
   * Be very careful when modifying this hook
   * @returns {void}
   */
  useAddToCartManager();

  useEffect(() => {
    if (cartDrawerOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [cartDrawerOpen]);

  const closeCartDrawer = useCallback(() => {
    toggleCartDrawer({
      cartDrawerOpen: false,
      cartDrawerUserToggled: false,
      giftNoteDrawerOpen: false,
      giftWithPurchaseDrawerOpen: false,
      productVariantDrawerOpen: false,
    });
  }, [toggleCartDrawer]);

  const setCartAnimationState = useCallback((cartDrawerAnimating: boolean) => {
    TablescapeContext.emit(TablescapeEvent.CART_ANIMATION_STATE, {
      ui: {
        cartDrawerAnimating,
      },
    });
  }, []);

  return (
    <>
      <Transition
        afterEnter={() => setCartAnimationState(false)}
        afterLeave={() => setCartAnimationState(false)}
        appear={true}
        as={Fragment}
        beforeEnter={() => setCartAnimationState(true)}
        beforeLeave={() => setCartAnimationState(true)}
        show={cartDrawerOpen}
      >
        <div className={styles.container}>
          {/* Background overlay */}
          <CartDrawerBackdrop closeCartDrawer={closeCartDrawer} />

          <div className={styles.wrapper}>
            {/* Cart Drawer */}
            <CartDrawer {...styles.drawer} />

            {/* Gift Note Drawer */}
            <CartGiftNoteDrawer
              show={cartDrawerOpen && giftNoteDrawerOpen}
              {...styles.drawer}
            />

            {/* Gift With Purchase Drawer */}
            <CartGiftWithPurchaseDrawer
              show={cartDrawerOpen && giftWithPurchaseDrawerOpen}
              {...styles.drawer}
            />

            {/* Product Variant Drawer */}
            <CartProductVariantDrawer
              show={
                !!(activeProduct && cartDrawerOpen && productVariantDrawerOpen)
              }
              {...styles.drawer}
            />
          </div>
        </div>
      </Transition>

      <CartDrawerAnalytics />
    </>
  );
};

export const CartDrawerManager = memo(CartDrawerManagerBase);
